/**
 * AM upload-on-behalf-of-merchant (Feb 21, 2026).
 *
 * AMs frequently receive documents over email mid-onboarding (a fresh
 * scan of an article, a bank statement Joe forgot to upload). Before
 * this button there was no way to push those files into the slot —
 * we had to bounce them back to the merchant which slowed onboarding
 * by a day. Now: click → file picker → POST → row supersedes + ai-check
 * fires + any open replacement-request is auto-resolved.
 *
 * Pill-shaped, sits next to "View file" on every KYB row. When the
 * slot already has a current upload the label changes to "Replace" —
 * same endpoint, just clearer copy.
 *
 * Extracted from DocumentsTable.jsx in the Feb-21 cleanup pass.
 */
import React, { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import api from "../../../lib/api";


export default function AdminUploadButton({ merchantId, docKey, hasCurrent, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const trigger = () => {
    if (busy) return;
    setError("");
    inputRef.current && inputRef.current.click();
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";  // allow re-uploading the same filename
    if (!file) return;
    setBusy(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(
        `/admin/merchants/${merchantId}/documents/${docKey}/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onUploaded && onUploaded();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed.");
      // Surface immediately so the AM understands what happened
      // (size, type, magic-byte mismatch all come through detail).
      alert(typeof detail === "string" ? detail : "Upload failed.");
    } finally { setBusy(false); }
  };

  const label = hasCurrent ? "Replace" : "Upload";

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.doc,.docx,.xls,.xlsx,application/pdf,image/png,image/jpeg,image/heic,image/heif"
        onChange={onFile}
        className="hidden"
        data-testid={`doc-admin-upload-input-${docKey}`}
      />
      <button
        type="button"
        onClick={trigger}
        disabled={busy}
        data-testid={`doc-admin-upload-${docKey}`}
        title={
          busy ? "Uploading…"
            : hasCurrent
              ? "Upload a new version on behalf of merchant (audit-logged)"
              : "Upload this document on behalf of merchant (audit-logged)"
        }
        className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-400/[0.04] px-2.5 py-1 text-[11px] text-emerald-200 hover:border-emerald-400/50 hover:bg-emerald-400/[0.10] transition-colors disabled:opacity-40 disabled:cursor-wait"
      >
        {busy
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <Upload className="h-3 w-3" />}
        {busy ? "Uploading…" : label}
      </button>
      {error && (
        <span
          data-testid={`doc-admin-upload-error-${docKey}`}
          className="text-[11px] text-rose-300 ml-1"
        >{error}</span>
      )}
    </>
  );
}
